/** Content of the barcelona package: object handlers and NPC talk. Registered through content/registry.ts. */
import { apply, burst, celebrate, give, onceForever, onceToday, toast } from '../core/effects';
import { S } from '../core/game';
import { m } from '../i18n';
import { npcName, objName } from '../i18n/labels';
import { sfx } from '../audio/sfx';
import { sayEnglish } from '../audio/speech';
import { say, type Choice } from '../ui/dialog';
import { bcFx } from '../render/propsBarcelona';
import { MAPS } from '../world/map';
import { registerObjects, registerTalk } from './registry';
import { order, quiz, travelTo, type FoodSpec } from './trips';

const bc = () => m().barcelona;
const pick = <T>(a: readonly T[]): T => a[Math.floor(Math.random() * a.length)];
const spots = () => MAPS.barcelona.spots!;
const H = (h: number, mm = 0) => h * 60 + mm;

/** A fact with one action button; `onAct` runs the reward and returns the follow-up line. */
function landmark(id: string, icon: string, text: string, act: string, onAct: () => string, en?: string): void {
  say({ who: objName(id), icon, text, en, choices: [{ text: act, run: () => say({ who: objName(id), icon, text: onAct(), en }) }, { text: m().common.back }] });
}

/** A fact that leads into a `quiz`; `reward` runs once per day on a right answer. */
function factQuiz(id: string, icon: string, text: string, act: string, t: { q: string; a: readonly string[]; right: string; wrong: string }, reward: () => void): void {
  say({
    who: objName(id),
    icon,
    text,
    choices: [{ text: act, run: () => quiz(objName(id), icon, text, t.q, t.a, t.right, t.wrong, () => onceToday(`bc.${id}`) && reward()) }, { text: m().common.back }],
  });
}

// --- Getting around ---------------------------------------------------------------------------

function metro(here: 'guell' | 'sagrada' | 'liceu' | 'beach'): void {
  const t = bc().metro;
  const stops: ['guell' | 'sagrada' | 'liceu' | 'beach' | 'airport', string][] = [['guell', t.guell], ['sagrada', t.sagrada], ['liceu', t.liceu], ['beach', t.beach], ['airport', t.airport]];
  say({
    who: objName(`bc_metro${here[0].toUpperCase()}${here.slice(1)}`),
    icon: '🚇',
    text: `${t.text}\n\n${t.fact}`,
    choices: [
      ...stops.filter(([k]) => k !== here).map(([k, label]): Choice => ({
        text: label,
        cost: m().common.minutes(12),
        run: () => {
          sfx('door');
          apply({ minutes: 12 }, { float: false });
          travelTo('barcelona', spots()[k]);
          window.setTimeout(() => toast(t.riding), 300);
        },
      })),
      { text: m().common.back },
    ],
  });
}

function aerobus(): void {
  const t = bc().aerobus;
  say({
    who: objName('bc_aerobus'),
    icon: '🚌',
    text: t.text,
    choices: [
      {
        text: t.go,
        cost: m().common.minutes(35),
        run: () => {
          sfx('door');
          apply({ minutes: 35 }, { float: false });
          travelTo('barcelona', spots().catalunya);
          toast(t.riding);
          window.setTimeout(() => say({ who: objName('bc_aerobus'), icon: '🚌', text: t.arrived }), 900);
        },
      },
      { text: m().common.back },
    ],
  });
}

// --- Sights -----------------------------------------------------------------------------------

function statue(): void {
  const t = bc().statue;
  say({
    who: objName('bc_statue'),
    icon: '🗿',
    text: t.text,
    choices: [
      {
        text: t.tip,
        run: () => {
          if (S().coins < 2) return toast(t.poor);
          apply({ coins: -2, mood: 8, minutes: 3 });
          sfx('pop');
          bcFx.statueUntil = Date.now() + 4000;
          burst('sparkle', 5);
          if (onceToday('bc.statue')) apply({ stars: 1 }, { float: false });
          say({ who: objName('bc_statue'), icon: '🗿', text: t.moved });
        },
      },
      { text: m().common.back },
    ],
  });
}

function petanca(): void {
  const t = bc().petanca;
  const throwIt = () => {
    sfx('drop');
    const cm = Math.floor(Math.random() * Math.random() * 60) + 1;
    const nums = m().taroko.swallows.numbers;
    if (cm <= 10) sayEnglish(nums[cm], 1);
    if (cm <= 8) {
      burst('sparkle', 6);
      if (onceToday('bc.petanca')) apply({ mood: 10, stars: 2 });
    } else apply({ minutes: 2 }, { float: false });
    say({ who: objName('bc_petanca'), icon: '🎯', text: `${t.result(cm)}${cm <= 8 ? `\n\n${t.great}` : ''}`, choices: [{ text: t.again, run: throwIt }, { text: m().common.back }] });
  };
  say({ who: objName('bc_petanca'), icon: '🎯', text: t.text, choices: [{ text: t.throw, run: throwIt }, { text: m().common.back }] });
}

/** A stall with several dishes: pick one, then order it in English. */
function menu(id: string, icon: string, text: string, dishes: [string, string, FoodSpec, { energy?: number; mood?: number }][]): void {
  say({
    who: objName(id),
    icon,
    text,
    choices: [
      ...dishes.map(([ic, name, spec, fx]): Choice => ({ text: `${ic} ${name}`, cost: m().world3.market.cost(spec.price), run: () => order(id as 'bc_boqueria', ic, spec, fx) })),
      { text: m().common.back },
    ],
  });
}

function boqueria(): void {
  const t = bc().boqueria;
  const time = S().time;
  if (time < H(8) || time >= H(15)) return say({ who: objName('bc_boqueria'), icon: '🍓', text: t.closed });
  menu('bc_boqueria', '🍓', t.text, [
    ['🥭', t.juice.ok.replace(/, please\.$/, ''), t.juice, { energy: 10, mood: 8 }],
    ['🍖', t.ham.ok.replace(/, please\.$/, ''), t.ham, { energy: 18, mood: 6 }],
    ['🍓', t.fruit.ok.replace(/, please\.$/, ''), t.fruit, { energy: 12, mood: 6 }],
  ]);
}

function chiringuito(): void {
  const t = bc().chiringuito;
  const time = S().time;
  const lunch = (time >= H(14) && time < H(16, 30)) || (time >= H(21) && time < H(23, 30));
  const dishes: [string, string, FoodSpec, { energy?: number; mood?: number }][] = [
    ['🍮', t.crema.ok.replace(/, please\.$/, ''), t.crema, { energy: 10, mood: 10 }],
    ['🥛', t.horchata.ok.replace(/, please\.$/, ''), t.horchata, { energy: 8, mood: 6 }],
  ];
  if (lunch) dishes.unshift(['🥘', t.paella.ok.replace(/, please\.$/, ''), t.paella, { energy: 40, mood: 14 }]);
  menu('bc_chiringuito', '🥘', lunch ? t.text : `${t.text}\n\n${t.noPaella}`, dishes);
}

registerObjects({
  bc_aerobus: aerobus,
  bc_metroGuell: () => metro('guell'),
  bc_metroSagrada: () => metro('sagrada'),
  bc_metroLiceu: () => metro('liceu'),
  bc_metroBeach: () => metro('beach'),
  bc_sagrada() {
    const t = bc().sagrada;
    factQuiz('bc_sagrada', '⛪', t.text, t.ask, t, () => {
      sayEnglish(t.phrase, 0.85);
      celebrate();
      apply({ study: 6, stars: 4, mood: 8 });
    });
  },
  bc_guellBench() {
    const t = bc().guellBench;
    say({
      who: objName('bc_guellBench'),
      icon: '🪑',
      text: t.text,
      choices: [
        {
          text: t.sit,
          run: () => {
            sayEnglish(t.phrase, 0.85);
            if (onceToday('bc.bench')) apply({ energy: 10, mood: 12, stars: 2, minutes: 10 });
            else apply({ energy: 5, minutes: 10 });
            quiz(objName('bc_guellBench'), '🪑', `${t.sat}\n\n“${t.phrase}”`, t.q, t.a, t.right, t.wrong, () => onceToday('bc.benchQuiz') && apply({ study: 5, stars: 2 }));
          },
        },
        { text: m().common.back },
      ],
    });
  },
  bc_dragon() {
    const t = bc().dragon;
    say({
      who: objName('bc_dragon'),
      icon: '🦎',
      text: t.text,
      choices: [
        {
          text: t.photo,
          run: () => {
            sfx('ok');
            burst('sparkle', 6);
            if (onceForever('bc.dragonPhoto')) {
              give('photo');
              apply({ mood: 12, stars: 4 });
            } else if (onceToday('bc.dragon')) apply({ mood: 6, stars: 1 });
            quiz(objName('bc_dragon'), '🦎', t.done, t.q, t.a, t.right, t.wrong, () => onceToday('bc.dragonQuiz') && apply({ study: 4, stars: 2 }));
          },
        },
        { text: m().common.back },
      ],
    });
  },
  bc_canaletes() {
    const t = bc().canaletes;
    landmark('bc_canaletes', '🚰', t.text, t.drink, () => {
      sfx('water');
      sayEnglish(t.phrase, 0.85);
      apply({ energy: 4, bladder: 8, minutes: 2 });
      if (onceToday('bc.canaletes')) apply({ mood: 8, stars: 2 }, { float: false });
      return `${t.drank}\n\n“${t.phrase}”`;
    }, t.phrase);
  },
  bc_flowers: () => order('bc_flowers', '💐', bc().flowers, { mood: 10 }, () => give('sunflower')),
  bc_statue: statue,
  bc_boqueria: boqueria,
  bc_columbus() {
    const t = bc().columbus;
    factQuiz('bc_columbus', '🗽', t.text, t.look, t, () => apply({ study: 5, stars: 2 }));
  },
  bc_cathedral() {
    const t = bc().cathedral;
    factQuiz('bc_cathedral', '⛪', t.text, t.geese, t, () => apply({ study: 5, stars: 3, mood: 5 }));
  },
  bc_xurros: () => order('bc_xurros', '🍫', bc().xurros, { energy: 22, mood: 12 }),
  bc_castellers() {
    const t = bc().castellers;
    say({
      who: objName('bc_castellers'),
      icon: '🧗',
      text: t.text,
      choices: [
        {
          text: t.cheer,
          run: () => {
            sfx('fanfare');
            celebrate();
            if (onceToday('bc.castell')) apply({ mood: 12, stars: 2 });
            quiz(objName('bc_castellers'), '🧗', t.cheered, t.q, t.a, t.right, t.wrong, () => onceToday('bc.castellQuiz') && apply({ study: 6, stars: 3 }));
          },
        },
        { text: m().common.back },
      ],
    });
  },
  bc_petanca: petanca,
  bc_chiringuito: chiringuito,
});

/** NPC talk: the lost tourist is a Chinese practice line; the rest share a line (quoted bits are English practice). */
registerTalk((id) => {
  const t = bc().talk;
  if (id === 'bc_tourist') {
    const r = bc().tourist;
    say({
      who: npcName(id),
      icon: '🧳',
      text: r.intro,
      choices: [
        { text: r.help, run: () => quiz(npcName(id), '🧳', r.intro, r.q, r.a, r.right, r.wrong, () => onceForever('bc.tourist') && (give('pineappleCake'), apply({ mood: 10, stars: 5, study: 4 }))) },
        { text: m().common.back },
      ],
    });
    return true;
  }
  const lines = (t as unknown as Record<string, readonly string[] | undefined>)[id];
  if (!id.startsWith('bc_') || !Array.isArray(lines)) return false;
  const icons: Record<string, string> = {
    bc_florist: '🌻', bc_guide: '⛪', bc_waiter: '🥘', bc_avi: '🥖', bc_busker: '🎸', bc_sardanista: '💃', bc_petancaMan: '🎯',
    bc_juicer: '🥭', bc_kid: '🛼', bc_staff: 'ℹ️', bc_casteller: '🧗', bc_painter: '🎨',
  };
  const line = pick(lines);
  const quoted = [...line.matchAll(/“([^”]+)”/g)].map((q) => q[1]).filter((q) => /^[\x20-\x7e’]+$/.test(q));
  const en = quoted.length ? quoted.join(' … ') : undefined;
  if (en) sayEnglish(en, 0.9);
  if (id === 'bc_busker') burst('note', 4);
  if (id === 'bc_waiter' || id === 'bc_avi') onceToday(`bc.chat.${id}`) && apply({ study: 2 }, { float: false });
  say({ who: npcName(id), icon: icons[id] ?? '💬', text: line, en });
  return true;
});
